/**
 * ProceduralAtlas — bakes every entity texture used by the renderer into the
 * Phaser TextureManager with the Canvas 2D API. No PNGs are loaded from disk;
 * the BootScene calls generate() once before the first scene starts.
 *
 * Visual direction: dark sci-fi tactical (Helldivers / The Ascent / Synthetik):
 * brushed metal plates, neon signal accents, bevelled rims, glow halos, soft
 * specular highlights. Frame keys + per-frame dimensions match what
 * SpriteLayer.ts and Renderer.ts expect — only the painted contents changed.
 *
 * generateExtras() (declared in ./ProceduralAtlasExtras) is called alongside
 * generate() and produces the new background / landmark / overlay textures
 * consumed by BackgroundLayer and LandmarkLayer.
 */

import type Phaser from 'phaser';

import {
  ATLAS_SCALE,
  C_DAMAGED,
  C_DAMAGED_BRIGHT,
  C_DARK,
  C_GOLD,
  C_HEADING,
  C_NEUTRAL,
  C_OUTLINE,
  C_P0,
  C_P0_BRIGHT,
  C_P1,
  C_P1_BRIGHT,
  C_PLATE,
  C_PLATE_LIGHT,
  C_TEAL,
  TWO_PI,
  bevelFill,
  circ,
  generateExtras,
  halo,
  hexPath,
  makeCanvas,
  octPath,
} from './ProceduralAtlasExtras.js';
import type { Ctx } from './ProceduralAtlasExtras.js';
import { genSquads } from './ProceduralSquads.js';

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function generate(scene: Phaser.Scene): void {
  genRelays(scene);
  genHQ(scene);
  genTurretDef(scene);
  genTurretMini(scene);
  genProduction(scene);
  genCampUnits(scene);
  generateExtras(scene);
  genSquads(scene);
}

// ---------------------------------------------------------------------------
// Relay control nodes — antenna pylons with a glowing signal disc
// ---------------------------------------------------------------------------

function drawRelay(ctx: Ctx, cx: number, cy: number, color: string, bright: string): void {
  ctx.save();
  // Outer signal halo
  halo(ctx, cx, cy, 42, color, 0.55);
  // Hex base plate — bevelled dark steel
  hexPath(ctx, cx, cy, 30);
  const plate = ctx.createLinearGradient(cx - 28, cy - 28, cx + 28, cy + 28);
  plate.addColorStop(0, C_PLATE_LIGHT);
  plate.addColorStop(0.5, C_PLATE);
  plate.addColorStop(1, C_DARK);
  ctx.fillStyle = plate;
  ctx.fill();
  ctx.strokeStyle = C_OUTLINE;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  // Inner darker hex (recessed)
  hexPath(ctx, cx, cy, 22);
  ctx.fillStyle = C_DARK;
  ctx.fill();
  // Bevelled colour ring (outer + inner shadow line)
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.2;
  ctx.globalAlpha = 0.95;
  circ(ctx, cx, cy, 26, null, color, 2.2);
  ctx.globalAlpha = 0.35;
  circ(ctx, cx, cy, 23.5, null, C_DARK, 1);
  // Cardinal antenna spires
  ctx.globalAlpha = 0.85;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  for (let i = 0; i < 4; i++) {
    const a = (i * Math.PI) / 2 + Math.PI / 4;
    const x1 = cx + Math.cos(a) * 18;
    const y1 = cy + Math.sin(a) * 18;
    const x2 = cx + Math.cos(a) * 28;
    const y2 = cy + Math.sin(a) * 28;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    // Tip nub
    circ(ctx, x2, y2, 1.8, color);
  }
  // Inner spinning grille (two thin rings + tick marks)
  ctx.globalAlpha = 0.55;
  circ(ctx, cx, cy, 16, null, color, 1);
  ctx.globalAlpha = 0.35;
  circ(ctx, cx, cy, 12, null, color, 0.8);
  ctx.globalAlpha = 0.7;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  for (let i = 0; i < 12; i++) {
    const a = (i * Math.PI) / 6;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * 12, cy + Math.sin(a) * 12);
    ctx.lineTo(cx + Math.cos(a) * 14, cy + Math.sin(a) * 14);
    ctx.stroke();
  }
  // Centre emitter bloom
  ctx.globalAlpha = 0.65;
  circ(ctx, cx, cy, 9, bright);
  ctx.globalAlpha = 1;
  circ(ctx, cx, cy, 5, '#ffffff');
  // Specular highlight (top-left)
  ctx.globalAlpha = 0.55;
  circ(ctx, cx - 2, cy - 2, 2, '#ffffff');
  // Corner bolts on hex
  ctx.globalAlpha = 0.7;
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3 - Math.PI / 6;
    const bx = cx + Math.cos(a) * 27;
    const by = cy + Math.sin(a) * 27;
    circ(ctx, bx, by, 1.4, '#3a4250');
  }
  ctx.restore();
}

function genRelays(scene: Phaser.Scene): void {
  const FW = 82, FH = 105;
  const FWS = FW * ATLAS_SCALE, FHS = FH * ATLAS_SCALE;
  const v: Array<[string, string, string]> = [
    ['relay_teal', C_TEAL, '#5ff0d0'],
    ['relay_neutral', C_NEUTRAL, '#8a95a3'],
    ['relay_p1', C_P1, C_P1_BRIGHT],
    ['relay_p0', C_P0, C_P0_BRIGHT],
    ['relay_gold', C_GOLD, '#fff298'],
  ];
  const tex = makeCanvas(scene, 'relays', FWS, FHS, v.length);
  tex.context.scale(ATLAS_SCALE, ATLAS_SCALE);
  v.forEach(([, c, b], i) => drawRelay(tex.context, i * FW + FW / 2, FH / 2, c, b));
  tex.refresh();
  v.forEach(([name], i) => tex.add(name, 0, i * FWS, 0, FWS, FHS));
}

// ---------------------------------------------------------------------------
// HQ Cores — armoured signal portal with a bright energy heart
// ---------------------------------------------------------------------------

function drawCore(ctx: Ctx, cx: number, cy: number, color: string, bright: string, damaged: boolean): void {
  ctx.save();
  const ring = damaged ? C_DAMAGED : color;
  const innerColor = damaged ? C_DAMAGED_BRIGHT : bright;
  // Keep the core visually huge, but make the mechanical parts one object.
  // The previous crenellated wall and four corner nodes read as detached bars
  // at game scale, especially when the HP bar crossed the top pieces.
  halo(ctx, cx, cy, 106, color, damaged ? 0.14 : 0.3);
  halo(ctx, cx, cy, 76, color, damaged ? 0.2 : 0.38);
  halo(ctx, cx, cy, 50, color, damaged ? 0.28 : 0.52);

  ctx.globalAlpha = damaged ? 0.34 : 0.6;
  circ(ctx, cx, cy, 66, null, ring, 3.2);
  ctx.globalAlpha = damaged ? 0.18 : 0.28;
  circ(ctx, cx, cy, 58, null, '#ffffff', 1.1);

  // Outer hex hull: six joined armor plates around the reactor body.
  ctx.globalAlpha = 1;
  for (let i = 0; i < 6; i++) {
    const a0 = (i * Math.PI) / 3 - Math.PI / 6;
    const a1 = ((i + 1) * Math.PI) / 3 - Math.PI / 6;
    const r1 = 56, r2 = 39;
    const x1 = cx + r1 * Math.cos(a0), y1 = cy + r1 * Math.sin(a0);
    const x2 = cx + r1 * Math.cos(a1), y2 = cy + r1 * Math.sin(a1);
    const x3 = cx + r2 * Math.cos(a1), y3 = cy + r2 * Math.sin(a1);
    const x4 = cx + r2 * Math.cos(a0), y4 = cy + r2 * Math.sin(a0);
    ctx.beginPath();
    ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.lineTo(x3, y3); ctx.lineTo(x4, y4); ctx.closePath();
    const mx = (x1 + x3) / 2, my = (y1 + y3) / 2;
    const grad = ctx.createRadialGradient(mx - 8, my - 8, 0, mx, my, 22);
    grad.addColorStop(0, C_PLATE_LIGHT);
    grad.addColorStop(1, damaged && i % 2 === 0 ? '#2a0808' : C_PLATE);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = C_OUTLINE;
    ctx.lineWidth = 1.4;
    ctx.stroke();
  }
  // Integrated beveled rim: one readable hull instead of loose plates.
  hexPath(ctx, cx, cy, 56);
  ctx.strokeStyle = ring;
  ctx.lineWidth = 4;
  ctx.globalAlpha = damaged ? 0.75 : 1;
  ctx.stroke();
  hexPath(ctx, cx, cy, 48);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1;
  ctx.globalAlpha = damaged ? 0.18 : 0.34;
  ctx.stroke();
  // Compact vertex anchors, small enough not to become a second core ring.
  ctx.globalAlpha = damaged ? 0.4 : 0.9;
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3 - Math.PI / 6;
    const bx = cx + 56 * Math.cos(a), by = cy + 56 * Math.sin(a);
    halo(ctx, bx, by, 9, color, 0.45);
    circ(ctx, bx, by, 3.6, ring);
    circ(ctx, bx, by, 1.7, '#ffffff');
  }
  // Inner hex shell
  hexPath(ctx, cx, cy, 37);
  ctx.fillStyle = C_DARK;
  ctx.globalAlpha = 1;
  ctx.fill();
  ctx.strokeStyle = ring;
  ctx.lineWidth = 2;
  ctx.globalAlpha = damaged ? 0.3 : 0.7;
  ctx.stroke();
  // Energy conduits from the reactor to the inner shell vertices.
  const vertexInner = 17;
  const vertexOuter = 33;
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3 - Math.PI / 6;
    const x1 = cx + Math.cos(a) * vertexInner;
    const y1 = cy + Math.sin(a) * vertexInner;
    const x2 = cx + Math.cos(a) * vertexOuter;
    const y2 = cy + Math.sin(a) * vertexOuter;
    halo(ctx, x2, y2, 7, innerColor, damaged ? 0.35 : 0.7);
    ctx.globalAlpha = damaged ? 0.40 : 0.85;
    ctx.strokeStyle = innerColor;
    ctx.lineWidth = 1.4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.globalAlpha = damaged ? 0.55 : 1;
    circ(ctx, x2, y2, 1.8, '#ffffff');
  }
  // Energy heart: strong center, but no longer an oversized flat white disc.
  ctx.globalAlpha = 1;
  halo(ctx, cx, cy, 32, innerColor, damaged ? 0.55 : 0.95);
  circ(ctx, cx, cy, 17, innerColor);
  ctx.globalAlpha = damaged ? 0.45 : 0.9;
  circ(ctx, cx, cy, 10, '#f4fbff');
  ctx.globalAlpha = damaged ? 0.26 : 0.5;
  circ(ctx, cx - 4, cy - 5, 4, '#ffffff');
  // Damaged cracks — scale with new fortress size
  if (damaged) {
    ctx.globalAlpha = 0.7;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      const a = i * 1.6;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a) * 36, cy + Math.sin(a) * 36);
      ctx.lineTo(cx + Math.cos(a + 0.6) * 56, cy + Math.sin(a + 0.6) * 56);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function genHQ(scene: Phaser.Scene): void {
  // Large frame leaves room for the soft atmospheric glow around the compact
  // integrated hull without bleeding into adjacent atlas frames.
  const FW = 224, FH = 286;
  const FWS = FW * ATLAS_SCALE, FHS = FH * ATLAS_SCALE;
  const v: Array<[string, string, string, boolean]> = [
    ['hq_neutral', C_NEUTRAL, '#8a95a3', false],
    ['hq_p0', C_P0, C_P0_BRIGHT, false],
    ['hq_p1', C_P1, C_P1_BRIGHT, false],
    ['hq_damaged', C_DAMAGED, C_DAMAGED_BRIGHT, true],
  ];
  const tex = makeCanvas(scene, 'hq', FWS, FHS, v.length);
  tex.context.scale(ATLAS_SCALE, ATLAS_SCALE);
  v.forEach(([, c, b, d], i) => drawCore(tex.context, i * FW + FW / 2, FH / 2, c, b, d));
  tex.refresh();
  v.forEach(([name], i) => tex.add(name, 0, i * FWS, 0, FWS, FHS));
}

// ---------------------------------------------------------------------------
// Permanent turrets — octagonal armour, scope optics, vents
// ---------------------------------------------------------------------------

function drawTurret(ctx: Ctx, cx: number, cy: number, color: string, bright: string, idle: boolean): void {
  ctx.save();
  const ring = idle ? '#3a4050' : color;
  // Soft shadow halo
  halo(ctx, cx, cy + 2, 32, '#000', 0.35);
  // Octagonal armoured base — bevelled
  octPath(ctx, cx, cy, 26);
  const grad = ctx.createLinearGradient(cx - 22, cy - 22, cx + 22, cy + 22);
  grad.addColorStop(0, C_PLATE_LIGHT);
  grad.addColorStop(0.5, C_PLATE);
  grad.addColorStop(1, C_DARK);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = ring;
  ctx.lineWidth = 2;
  ctx.stroke();
  // Inner rim
  octPath(ctx, cx, cy, 20);
  ctx.strokeStyle = C_OUTLINE;
  ctx.lineWidth = 1;
  ctx.stroke();
  // Mount bolts
  for (let i = 0; i < 4; i++) {
    const a = (i * Math.PI) / 2 + Math.PI / 4;
    circ(ctx, cx + Math.cos(a) * 21, cy + Math.sin(a) * 21, 2.2, '#1c2129', C_OUTLINE, 0.8);
    circ(ctx, cx + Math.cos(a) * 21 - 0.6, cy + Math.sin(a) * 21 - 0.6, 0.7, '#5a6470');
  }
  // Pivot dome — radial gradient
  const dome = ctx.createRadialGradient(cx - 3, cy - 3, 1, cx, cy, 11);
  dome.addColorStop(0, idle ? '#3a4250' : bright);
  dome.addColorStop(0.6, idle ? '#1e2330' : color);
  dome.addColorStop(1, C_DARK);
  ctx.fillStyle = dome;
  circ(ctx, cx, cy, 11, null);
  ctx.beginPath(); ctx.arc(cx, cy, 11, 0, TWO_PI); ctx.fill();
  ctx.strokeStyle = ring;
  ctx.lineWidth = 1.4;
  circ(ctx, cx, cy, 11, null, ring, 1.4);
  // Scope optic
  ctx.globalAlpha = idle ? 0.5 : 1;
  circ(ctx, cx, cy, 4.5, idle ? '#1a1e26' : '#000');
  circ(ctx, cx, cy, 4.5, null, ring, 0.8);
  ctx.globalAlpha = idle ? 0.4 : 0.85;
  circ(ctx, cx - 1.2, cy - 1.2, 1.4, '#ffffff');
  // Vent ribs on the rim
  ctx.globalAlpha = 0.5;
  ctx.strokeStyle = C_OUTLINE;
  ctx.lineWidth = 1;
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath();
    ctx.moveTo(cx - 14, cy + i * 4);
    ctx.lineTo(cx - 18, cy + i * 4);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 14, cy + i * 4);
    ctx.lineTo(cx + 18, cy + i * 4);
    ctx.stroke();
  }
  ctx.restore();
}

function genTurretDef(scene: Phaser.Scene): void {
  const FW = 72, FH = 70;
  const FWS = FW * ATLAS_SCALE, FHS = FH * ATLAS_SCALE;
  const v: Array<[string, string, string, boolean]> = [
    ['turret_idle', C_NEUTRAL, '#8a95a3', true],
    ['turret_p0', C_P0, C_P0_BRIGHT, false],
    ['turret_p1', C_P1, C_P1_BRIGHT, false],
  ];
  const tex = makeCanvas(scene, 'turret_def', FWS, FHS, v.length);
  tex.context.scale(ATLAS_SCALE, ATLAS_SCALE);
  v.forEach(([, c, b, idle], i) => drawTurret(tex.context, i * FW + FW / 2, FH / 2, c, b, idle));
  tex.refresh();
  v.forEach(([name], i) => tex.add(name, 0, i * FWS, 0, FWS, FHS));
}

// ---------------------------------------------------------------------------
// Mini-turrets — compact emplacement
// ---------------------------------------------------------------------------

function drawMini(ctx: Ctx, cx: number, cy: number, color: string, bright: string, active: boolean): void {
  ctx.save();
  const ring = active ? color : '#3a4050';
  halo(ctx, cx, cy + 1, 20, '#000', 0.3);
  if (active) halo(ctx, cx, cy, 22, color, 0.45);
  // Base disc
  circ(ctx, cx, cy, 16, C_PLATE, ring, 1.5);
  // Inner darker disc with bevel
  const dome = ctx.createRadialGradient(cx - 2, cy - 2, 1, cx, cy, 9);
  dome.addColorStop(0, active ? bright : '#1e2330');
  dome.addColorStop(0.7, active ? color : '#171b24');
  dome.addColorStop(1, C_DARK);
  ctx.fillStyle = dome;
  ctx.beginPath(); ctx.arc(cx, cy, 9, 0, TWO_PI); ctx.fill();
  // Running light
  if (active) {
    ctx.globalAlpha = 0.85;
    circ(ctx, cx, cy, 3, '#ffffff');
  }
  // Tri-bolt
  for (let i = 0; i < 3; i++) {
    const a = (i * TWO_PI) / 3 + Math.PI / 6;
    circ(ctx, cx + Math.cos(a) * 13, cy + Math.sin(a) * 13, 1.4, '#3a4250');
  }
  ctx.restore();
}

function genTurretMini(scene: Phaser.Scene): void {
  const FW = 50, FH = 63;
  const FWS = FW * ATLAS_SCALE, FHS = FH * ATLAS_SCALE;
  const v: Array<[string, string, string, boolean]> = [
    ['mini_base', C_NEUTRAL, '#8a95a3', false],
    ['mini_idle', C_NEUTRAL, '#8a95a3', false],
    ['mini_active', C_P0, C_P0_BRIGHT, true],
  ];
  const tex = makeCanvas(scene, 'turret_mini', FWS, FHS, v.length);
  tex.context.scale(ATLAS_SCALE, ATLAS_SCALE);
  v.forEach(([, c, b, act], i) => drawMini(tex.context, i * FW + FW / 2, FH / 2, c, b, act));
  tex.refresh();
  v.forEach(([name], i) => tex.add(name, 0, i * FWS, 0, FWS, FHS));
}

// ---------------------------------------------------------------------------
// Production hubs — signal-tower with dish + antenna
// ---------------------------------------------------------------------------

function drawHub(ctx: Ctx, cx: number, cy: number, color: string, bright: string, idle: boolean): void {
  ctx.save();
  // V1.8.1 simplification — the previous redesign was visually competing
  // with relays and cores (cargo-bay doors + bright accent rim + halo +
  // central energy emitter + white pinpoint + vertex bolts). Hubs are
  // SUPPORT structures and should sit visually below relays. New design
  // is a quiet utilitarian broadcast box: simple octagonal plate, ONE
  // small dim team-color marker at the centre, no halos, no white core.
  const accent = idle ? '#3a4050' : color;

  // 1) Subtle drop shadow only — no team-colour floor halo (was making
  // hubs glow as brightly as relays).
  halo(ctx, cx, cy + 3, 36, '#000', 0.40);

  // 2) Outer octagonal armoured plate.
  octPath(ctx, cx, cy, 30);
  const plateGrad = ctx.createLinearGradient(cx - 28, cy - 28, cx + 28, cy + 28);
  plateGrad.addColorStop(0,    C_PLATE_LIGHT);
  plateGrad.addColorStop(0.55, C_PLATE);
  plateGrad.addColorStop(1,    C_DARK);
  ctx.fillStyle = plateGrad;
  ctx.fill();
  ctx.strokeStyle = C_OUTLINE;
  ctx.lineWidth = 1.4;
  ctx.stroke();
  // Dim team-colour accent — alpha cut roughly in half so the plate
  // identifies the owner without shouting.
  ctx.strokeStyle = accent;
  ctx.lineWidth = 1;
  ctx.globalAlpha = idle ? 0.25 : 0.45;
  octPath(ctx, cx, cy, 26);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // 3) Inner darker octagonal recess — sells "this plate has a sunk centre."
  octPath(ctx, cx, cy, 16);
  ctx.fillStyle = '#0c1118';
  ctx.fill();
  ctx.strokeStyle = accent;
  ctx.lineWidth = 0.7;
  ctx.globalAlpha = idle ? 0.30 : 0.55;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // 4) Small central emitter dot — dim team colour, NO halo, NO white
  // pinpoint. This is just an "I'm online" indicator, not a focal point.
  if (!idle) {
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(cx, cy, 3.5, 0, TWO_PI); ctx.fill();
    ctx.fillStyle = bright;
    ctx.beginPath(); ctx.arc(cx, cy, 1.6, 0, TWO_PI); ctx.fill();
  } else {
    ctx.fillStyle = '#2a3040';
    ctx.beginPath(); ctx.arc(cx, cy, 3, 0, TWO_PI); ctx.fill();
  }

  // 5) Four small radial ribs — a quiet "this is industrial gear" detail.
  ctx.globalAlpha = 0.40;
  ctx.strokeStyle = '#3a4250';
  ctx.lineWidth = 0.8;
  for (let k = 0; k < 4; k++) {
    const a = (k * Math.PI) / 2 + Math.PI / 4;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * 17, cy + Math.sin(a) * 17);
    ctx.lineTo(cx + Math.cos(a) * 26, cy + Math.sin(a) * 26);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

function genProduction(scene: Phaser.Scene): void {
  const FW = 82, FH = 125;
  const FWS = FW * ATLAS_SCALE, FHS = FH * ATLAS_SCALE;
  const v: Array<[string, string, string, boolean]> = [
    ['hub_idle', C_NEUTRAL, '#8a95a3', true],
    ['hub_p0', C_P0, C_P0_BRIGHT, false],
    ['hub_p1', C_P1, C_P1_BRIGHT, false],
  ];
  const tex = makeCanvas(scene, 'production', FWS, FHS, v.length);
  tex.context.scale(ATLAS_SCALE, ATLAS_SCALE);
  v.forEach(([, c, b, idle], i) => drawHub(tex.context, i * FW + FW / 2, FH / 2, c, b, idle));
  tex.refresh();
  v.forEach(([name], i) => tex.add(name, 0, i * FWS, 0, FWS, FHS));
}

// ---------------------------------------------------------------------------
// Neutral camp turret — teal-lit hex emplacement, visually distinct from
// all player-owned buildings. Guards the mid relay in every map that has
// `enableNeutralCamps: true`. Uses hex base (player turrets use octagon)
// and a teal energy core so the affiliation reads instantly.
// ---------------------------------------------------------------------------

function drawCampTurret(ctx: Ctx, cx: number, cy: number): void {
  ctx.save();
  // Drop shadow
  halo(ctx, cx, cy + 2, 34, '#000', 0.4);
  // Teal hostile-emplacement glow
  halo(ctx, cx, cy, 34, C_TEAL, 0.35);
  // Hexagonal armoured base — a different shape from player's octagonal turrets
  hexPath(ctx, cx, cy, 28);
  const grad = ctx.createLinearGradient(cx - 24, cy - 24, cx + 24, cy + 24);
  grad.addColorStop(0, C_PLATE_LIGHT);
  grad.addColorStop(0.55, C_PLATE);
  grad.addColorStop(1, C_DARK);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = C_TEAL;
  ctx.lineWidth = 2;
  ctx.globalAlpha = 0.9;
  ctx.stroke();
  // Inner hex rim (recessed detail)
  hexPath(ctx, cx, cy, 21);
  ctx.strokeStyle = C_OUTLINE;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 1;
  ctx.stroke();
  // Hex vertex bolts — teal-tipped (player turrets use neutral grey bolts)
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3 - Math.PI / 6;
    circ(ctx, cx + Math.cos(a) * 24, cy + Math.sin(a) * 24, 2, '#1c2129', C_TEAL, 0.7);
  }
  // Central dome — teal energy core
  const dome = ctx.createRadialGradient(cx - 3, cy - 3, 1, cx, cy, 12);
  dome.addColorStop(0, '#1de0bb');
  dome.addColorStop(0.55, C_TEAL);
  dome.addColorStop(1, C_DARK);
  ctx.fillStyle = dome;
  ctx.beginPath(); ctx.arc(cx, cy, 12, 0, TWO_PI); ctx.fill();
  ctx.strokeStyle = C_TEAL;
  ctx.lineWidth = 1.5;
  circ(ctx, cx, cy, 12, null, C_TEAL, 1.5);
  // Scope/iris
  circ(ctx, cx, cy, 5, '#0e1218');
  ctx.globalAlpha = 0.9;
  circ(ctx, cx, cy, 5, null, C_TEAL, 0.9);
  // Specular highlight
  ctx.globalAlpha = 0.7;
  circ(ctx, cx - 1.5, cy - 1.5, 1.5, '#ffffff');
  // Industrial vent slits — rougher than player turret ribs
  ctx.globalAlpha = 0.55;
  ctx.strokeStyle = C_OUTLINE;
  ctx.lineWidth = 1;
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath();
    ctx.moveTo(cx - 15, cy + i * 3.5);
    ctx.lineTo(cx - 21, cy + i * 3.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 15, cy + i * 3.5);
    ctx.lineTo(cx + 21, cy + i * 3.5);
    ctx.stroke();
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Neutral camp patrol drone — a hex-bodied hover sentinel with a teal energy
// core, twin forward barrels, side hover vents and an antenna beacon. Drawn
// facing +X so SpriteLayer can rotate by `u.facing`. Shares the hex shape
// language with `drawCampTurret` so the whole neutral faction reads as a
// cohesive "hex-clan" defensive force, distinct from both player squad
// silhouettes (diamond / wide-hex / boxy / circle-shield).
// ---------------------------------------------------------------------------

function drawCampPatrol(ctx: Ctx, cx: number, cy: number): void {
  ctx.save();
  // Ground shadow — soft gradient ellipse beneath the drone.
  ctx.save();
  ctx.translate(cx + 1, cy + 3);
  ctx.scale(1, 7 / 18);
  const shadowGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 18);
  shadowGrad.addColorStop(0, 'rgba(0,0,0,0.55)');
  shadowGrad.addColorStop(0.55, 'rgba(0,0,0,0.22)');
  shadowGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = shadowGrad;
  ctx.fillRect(-18, -18, 36, 36);
  ctx.restore();

  // Hover energy aura (teal) — sits under the chassis so it bleeds outward.
  halo(ctx, cx, cy, 26, C_TEAL, 0.55);

  // Rear hover-thruster glow (back-left), additive cyan plume.
  const thr = ctx.createLinearGradient(cx - 22, cy, cx - 8, cy);
  thr.addColorStop(0, 'rgba(0,212,170,0)');
  thr.addColorStop(0.55, 'rgba(80,240,210,0.35)');
  thr.addColorStop(1, 'rgba(180,255,235,0.7)');
  ctx.fillStyle = thr;
  ctx.beginPath();
  ctx.moveTo(cx - 20, cy - 5); ctx.lineTo(cx - 6, cy - 7);
  ctx.lineTo(cx - 6, cy + 7);  ctx.lineTo(cx - 20, cy + 5);
  ctx.closePath(); ctx.fill();

  // Swept-back stabiliser fins (top + bottom).
  ctx.fillStyle = '#152024';
  for (const sy of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(cx - 9, cy + sy * 7);
    ctx.lineTo(cx + 1, cy + sy * 12);
    ctx.lineTo(cx + 8, cy + sy * 9);
    ctx.lineTo(cx - 1, cy + sy * 6);
    ctx.closePath(); ctx.fill();
    ctx.strokeStyle = 'rgba(0,212,170,0.55)'; ctx.lineWidth = 0.9; ctx.stroke();
  }

  // Main hex chassis — point-forward, bevelled metal with teal trim.
  ctx.beginPath();
  ctx.moveTo(cx + 16,  cy);          // nose
  ctx.lineTo(cx + 8,   cy - 12);     // front-top
  ctx.lineTo(cx - 10,  cy - 12);     // rear-top
  ctx.lineTo(cx - 16,  cy);          // tail
  ctx.lineTo(cx - 10,  cy + 12);     // rear-bottom
  ctx.lineTo(cx + 8,   cy + 12);     // front-bottom
  ctx.closePath();
  const body = ctx.createLinearGradient(cx - 14, cy - 12, cx + 16, cy + 12);
  body.addColorStop(0, C_PLATE_LIGHT);
  body.addColorStop(0.45, C_PLATE);
  body.addColorStop(1, C_DARK);
  ctx.fillStyle = body;
  ctx.fill();
  ctx.strokeStyle = C_OUTLINE; ctx.lineWidth = 1.5; ctx.stroke();
  // Bright teal rim along the front-top edge.
  ctx.strokeStyle = C_TEAL; ctx.lineWidth = 1.4; ctx.globalAlpha = 0.85;
  ctx.beginPath();
  ctx.moveTo(cx - 9, cy - 11.5); ctx.lineTo(cx + 8, cy - 11.5); ctx.lineTo(cx + 15.5, cy);
  ctx.stroke(); ctx.globalAlpha = 1;

  // Panel seams along the spine + hex bolts at vertices.
  ctx.strokeStyle = C_OUTLINE; ctx.lineWidth = 0.8; ctx.globalAlpha = 0.55;
  ctx.beginPath();
  ctx.moveTo(cx - 8, cy - 11); ctx.lineTo(cx - 8, cy + 11);
  ctx.moveTo(cx + 1, cy - 12); ctx.lineTo(cx + 1, cy + 12);
  ctx.stroke(); ctx.globalAlpha = 1;
  ctx.fillStyle = '#1a2028';
  for (const [bx, by] of [[-9, -10], [-9, 10], [7, -10], [7, 10]] as [number, number][]) {
    ctx.beginPath(); ctx.arc(cx + bx, cy + by, 1.1, 0, TWO_PI); ctx.fill();
  }

  // Side heat vents (teal-lit slits) — two per side.
  ctx.strokeStyle = C_TEAL; ctx.lineWidth = 0.9; ctx.globalAlpha = 0.75;
  for (const sy of [-1, 1]) {
    for (let k = 0; k < 2; k++) {
      ctx.beginPath();
      ctx.moveTo(cx - 4 + k * 5, cy + sy * 8);
      ctx.lineTo(cx - 1 + k * 5, cy + sy * 11);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;

  // Central teal energy core — halo + dark socket + bright dot.
  halo(ctx, cx + 1, cy, 10, C_TEAL, 0.85);
  circ(ctx, cx + 1, cy, 4.5, '#08191a');
  ctx.strokeStyle = C_TEAL; ctx.lineWidth = 1.2;
  circ(ctx, cx + 1, cy, 4.5, null, C_TEAL, 1.2);
  circ(ctx, cx + 1, cy, 2.4, '#5cf4d4');
  ctx.globalAlpha = 0.9; circ(ctx, cx + 1.6, cy - 0.6, 0.9, '#ffffff'); ctx.globalAlpha = 1;

  // Forward sensor visor — dark slit with teal inner glow.
  ctx.fillStyle = '#000';
  ctx.beginPath(); ctx.ellipse(cx + 9, cy, 4, 1.8, 0, 0, TWO_PI); ctx.fill();
  ctx.fillStyle = C_TEAL; ctx.globalAlpha = 0.95;
  ctx.beginPath(); ctx.ellipse(cx + 10, cy, 2.7, 1.0, 0, 0, TWO_PI); ctx.fill();
  ctx.globalAlpha = 1; circ(ctx, cx + 11.3, cy - 0.4, 0.7, '#ffffff');

  // Twin forward laser barrels — short stubby cannons either side of the nose.
  ctx.fillStyle = '#1a212c';
  for (const sy of [-1, 1]) {
    ctx.fillRect(cx + 12, cy + sy * 4 - 1.1, 11, 2.2);
    ctx.strokeStyle = '#050708'; ctx.lineWidth = 0.7;
    ctx.strokeRect(cx + 12, cy + sy * 4 - 1.1, 11, 2.2);
    // Muzzle aperture
    circ(ctx, cx + 22.5, cy + sy * 4, 1.2, '#000');
    // Faint heat-glow at the breech
    halo(ctx, cx + 13, cy + sy * 4, 4, C_TEAL, 0.55);
  }

  // Dorsal antenna spike with beacon dot (rear-top).
  ctx.strokeStyle = '#3a4a5a'; ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 5, cy - 12); ctx.lineTo(cx - 5, cy - 19); ctx.stroke();
  halo(ctx, cx - 5, cy - 19, 5, C_TEAL, 0.85);
  circ(ctx, cx - 5, cy - 19, 1.3, '#aaffe5');

  // Top rim light — bright pixel along the leading edge.
  ctx.globalAlpha = 0.55; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 9, cy - 11.5); ctx.lineTo(cx + 7, cy - 11.5); ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.restore();
}

function genCampUnits(scene: Phaser.Scene): void {
  const FW = 72, FH = 70;
  const FWS = FW * ATLAS_SCALE, FHS = FH * ATLAS_SCALE;
  const v: Array<[string, (ctx: Ctx, cx: number, cy: number) => void]> = [
    ['camp_turret', drawCampTurret],
    ['camp_patrol', drawCampPatrol],
  ];
  const tex = makeCanvas(scene, 'camp_units', FWS, FHS, v.length);
  tex.context.scale(ATLAS_SCALE, ATLAS_SCALE);
  v.forEach(([, fn], i) => fn(tex.context, i * FW + FW / 2, FH / 2));
  tex.refresh();
  v.forEach(([name], i) => tex.add(name, 0, i * FWS, 0, FWS, FHS));
}
